import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  FileText,
  Clock,
  Calendar,
  DollarSign,
  HelpCircle,
  AlertCircle,
  Download,
  ExternalLink,
  Building2,
  Users,
  Shield,
  FileCheck,
  Briefcase,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MainLayout from "@/components/layouts/MainLayout";

const CompanyRegistration = () => {
  return (
    <>
      <Helmet>
        <title>Company Registration | Numberz Insight</title>
        <meta
          name="description"
          content="Register your company with Numberz Insight. Professional assistance with company registration, ASIC compliance, and director ID requirements. Updated for 2025 requirements."
        />
        <meta
          name="keywords"
          content="company registration, Australian company, business registration, ASIC, director ID, Numberz Insight, proprietary limited, 2025"
        />
      </Helmet>

      <MainLayout>
        <main className="flex-1">
          {/* Hero Section */}
          <section className="relative py-20 overflow-hidden bg-gradient-to-b from-background to-background/80 dark:from-background dark:to-background/90">
            <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                  <Badge className="mb-4 text-sm py-1 px-3" variant="outline">
                    Updated for 2025
                  </Badge>
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
                    Australian Company Registration
                  </h1>
                  <p className="text-xl text-muted-foreground">
                    Register your company with expert guidance from Numberz
                    Insight. We handle the paperwork so you can focus on
                    building your business.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button size="lg" className="gap-2">
                      Start Company Registration{" "}
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="gap-2"
                      asChild
                    >
                      <Link to="/contact">Get Expert Advice</Link>
                    </Button>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="rounded-xl overflow-hidden shadow-xl"
                >
                  <img
                    src="https://images.unsplash.com/photo-1664575599736-c5197c684128?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                    alt="Business professionals in a meeting"
                    className="w-full h-auto object-cover"
                  />
                </motion.div>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="py-16 bg-muted/30">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Benefits of Company Registration
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Registering a company provides numerous advantages for your
                  business operations and growth potential.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Shield className="h-5 w-5 mr-2 text-primary" />
                      Limited Liability
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Protect your personal assets with the limited liability
                      structure, separating your business and personal finances.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Building2 className="h-5 w-5 mr-2 text-primary" />
                      Professional Image
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Enhance your business credibility with customers,
                      suppliers, and potential investors with a registered
                      company.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Briefcase className="h-5 w-5 mr-2 text-primary" />
                      Business Continuity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      A company continues to exist regardless of changes in
                      ownership, providing long-term business stability.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Process Section */}
          <section className="py-16">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Company Registration Process
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Our streamlined process makes registering your company simple
                  and efficient.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-bold">1</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        Choose Your Company Structure
                      </h3>
                      <p className="text-muted-foreground">
                        Decide between a proprietary limited company (Pty Ltd)
                        or public company (Ltd) based on your business needs and
                        goals.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-bold">2</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        Select a Company Name
                      </h3>
                      <p className="text-muted-foreground">
                        Choose a unique name that reflects your business and
                        check its availability with ASIC. Your name must include
                        "Pty Ltd" for proprietary companies.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-bold">3</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        Appoint Directors and Shareholders
                      </h3>
                      <p className="text-muted-foreground">
                        Identify your company directors (minimum one) and
                        shareholders. Directors must have a Director ID and at
                        least one director must reside in Australia.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-bold">4</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        Determine Governance Rules
                      </h3>
                      <p className="text-muted-foreground">
                        Choose between using replaceable rules from the
                        Corporations Act or creating a company constitution to
                        govern your company.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-bold">5</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        Submit Registration
                      </h3>
                      <p className="text-muted-foreground">
                        Complete the registration process through ASIC or with
                        our expert assistance to ensure all requirements are
                        met.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-bold">6</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        Receive Company Documents
                      </h3>
                      <p className="text-muted-foreground">
                        Once approved, you'll receive your Certificate of
                        Registration, Australian Company Number (ACN), and
                        corporate key.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Requirements Section */}
          <section className="py-16 bg-muted/30">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Key Requirements</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Essential requirements for registering a company in Australia
                  in 2025.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Users className="h-5 w-5 mr-2 text-primary" />
                      Director Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <h4 className="font-medium">
                        Director Identification Number (Director ID)
                      </h4>
                      <p className="text-muted-foreground">
                        All company directors must apply for a Director ID
                        before they can be appointed. This is a unique
                        identifier that stays with you for life.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium">Residency Requirements</h4>
                      <p className="text-muted-foreground">
                        Proprietary companies must have at least one director
                        who ordinarily resides in Australia. Public companies
                        require at least two Australian resident directors.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium">Age Requirements</h4>
                      <p className="text-muted-foreground">
                        All directors must be at least 18 years of age.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileCheck className="h-5 w-5 mr-2 text-primary" />
                      Company Structure Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <h4 className="font-medium">Registered Office</h4>
                      <p className="text-muted-foreground">
                        Your company must have a registered office address in
                        Australia where communications can be sent.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium">Share Structure</h4>
                      <p className="text-muted-foreground">
                        Proprietary companies must have at least one shareholder
                        and no more than 50 non-employee shareholders.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium">Company Name</h4>
                      <p className="text-muted-foreground">
                        Your company name must end with 'Proprietary Limited'
                        (or 'Pty Ltd') and be unique from other registered
                        companies.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Costs and Timeframes Section */}
          <section className="py-16">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Costs and Timeframes
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Understanding the costs and timeframes involved in company
                  registration.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <DollarSign className="h-5 w-5 mr-2 text-primary" />
                      Registration Costs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-4">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium">
                            ASIC Registration Fee:
                          </span>
                          <p className="text-muted-foreground">
                            Standard proprietary company registration fee
                            (varies based on company type).
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium">
                            Annual Review Fee:
                          </span>
                          <p className="text-muted-foreground">
                            Annual fee to maintain your company registration
                            with ASIC.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium">
                            Professional Service Fees:
                          </span>
                          <p className="text-muted-foreground">
                            Optional fees for professional assistance with
                            registration (like our services).
                          </p>
                        </div>
                      </li>
                    </ul>
                  </CardContent>
                  <CardFooter>
                    <Button variant="outline" className="w-full" asChild>
                      <Link to="/contact">Get a Detailed Quote</Link>
                    </Button>
                  </CardFooter>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Clock className="h-5 w-5 mr-2 text-primary" />
                      Timeframes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-4">
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium">
                            Standard Registration:
                          </span>
                          <p className="text-muted-foreground">
                            Typically 1-2 business days after submitting all
                            required information.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium">
                            Director ID Application:
                          </span>
                          <p className="text-muted-foreground">
                            Allow additional time for directors to obtain their
                            Director ID before registration.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium">
                            Post-Registration Setup:
                          </span>
                          <p className="text-muted-foreground">
                            Additional time may be needed for ABN registration,
                            tax registrations, and bank account setup.
                          </p>
                        </div>
                      </li>
                    </ul>
                  </CardContent>
                  <CardFooter>
                    <Button variant="outline" className="w-full" asChild>
                      <Link to="/contact">Expedite Your Registration</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-16 bg-muted/30">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Common questions about company registration in Australia.
                </p>
              </div>

              <div className="max-w-3xl mx-auto">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger>
                      What's the difference between a company and a business
                      name?
                    </AccordionTrigger>
                    <AccordionContent>
                      A company is a separate legal entity that can enter into
                      contracts, sue and be sued, and has perpetual succession.
                      A business name is simply a name under which you conduct
                      business and doesn't create a separate legal entity.
                      Registering a business name doesn't provide the legal
                      protections that come with company registration.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-2">
                    <AccordionTrigger>
                      Do I need a Director ID to register a company?
                    </AccordionTrigger>
                    <AccordionContent>
                      Yes, as of 2022, all company directors must have a
                      Director ID before they can be appointed as a director.
                      This is a unique identifier that stays with you for life,
                      even if you change companies or stop being a director. You
                      can apply for a Director ID through the Australian
                      Business Registry Services (ABRS).
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-3">
                    <AccordionTrigger>
                      What's the difference between an ACN and an ABN?
                    </AccordionTrigger>
                    <AccordionContent>
                      An Australian Company Number (ACN) is issued when you
                      register a company with ASIC. An Australian Business
                      Number (ABN) is required for businesses to interact with
                      the Australian Taxation Office (ATO) and is necessary for
                      GST registration. Companies typically need both an ACN and
                      an ABN, and the ABN for a company will include the ACN
                      within it.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-4">
                    <AccordionTrigger>
                      What ongoing obligations do I have after registering a
                      company?
                    </AccordionTrigger>
                    <AccordionContent>
                      After registration, companies must maintain up-to-date
                      records, pay annual review fees to ASIC, notify ASIC of
                      any changes to company details, lodge annual financial
                      reports (for larger companies), and comply with tax
                      obligations. Directors also have legal duties under the
                      Corporations Act, including acting in good faith and in
                      the best interests of the company.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-5">
                    <AccordionTrigger>
                      Can I register a company online?
                    </AccordionTrigger>
                    <AccordionContent>
                      Yes, you can register a company online through the
                      Australian Government's Business Registration Service or
                      through a private service provider like Numberz Insight.
                      Online registration is typically faster and more
                      convenient than paper-based registration.
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-6">
                    <AccordionTrigger>
                      How do I protect my company name?
                    </AccordionTrigger>
                    <AccordionContent>
                      Registering a company name with ASIC prevents others from
                      registering an identical company name, but it doesn't
                      provide full brand protection. For comprehensive
                      protection, you should consider registering your company
                      name as a trademark with IP Australia, which gives you
                      exclusive rights to use that name in your industry across
                      Australia.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-primary/5 dark:bg-primary/10">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-4">
                    Ready to Register Your Company?
                  </h2>
                  <p className="text-xl text-muted-foreground mb-6">
                    Our team of professionals is ready to help you with your
                    company registration. Contact us today to get started.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                      <span>Expert guidance throughout the process</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                      <span>Fast and efficient service</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-primary" />
                      <span>Ongoing support for your business</span>
                    </div>
                  </div>
                </div>
                <div>
                  <Card>
                    <CardHeader>
                      <CardTitle>Start Your Company Registration</CardTitle>
                      <CardDescription>
                        Fill out the form below and one of our professionals
                        will contact you shortly.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <Button className="w-full" size="lg">
                          Start Registration
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full"
                          size="lg"
                          asChild
                        >
                          <Link to="/contact">Contact Us</Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Resources Section */}
          <section className="py-16">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Official Resources</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Helpful links to official government resources for company
                  registration.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      ASIC
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Australian Securities and Investments Commission - the
                      government body responsible for company registrations.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://asic.gov.au/for-business/registering-a-company/steps-to-register-a-company/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit ASIC <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      Business.gov.au
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Government resources for businesses, including company
                      registration information.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://business.gov.au/registrations/register-a-company"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit Business.gov.au{" "}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      ABRS
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Australian Business Registry Services - for director ID
                      applications and business registrations.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <a
                        href="https://www.abrs.gov.au/director-identification-number"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit ABRS <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        </main>
      </MainLayout>
    </>
  );
};

export default CompanyRegistration;
